import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { UserCog, Plus, Pencil, Ban, CheckCircle, Search, Loader2, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { MEDICAL_SPECIALISTS, getSpecialistBadgeStyle } from '../utils/specialists';

export default function DoctorListPage() {
  const { user } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [popularDoctors, setPopularDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlineOnlyFilter, setOnlineOnlyFilter] = useState(false);
  const [specialistFilter, setSpecialistFilter] = useState('');
  const [clinicFilter, setClinicFilter] = useState('');
  const [selectedClinics, setSelectedClinics] = useState([]); // Multi-select for superadmin
  // Bulk import state REMOVED - feature disabled per user request
  
  // Search states for dropdowns
  const [specialistSearch, setSpecialistSearch] = useState('');
  const [clinicSearch, setClinicSearch] = useState('');
  const [showSpecialistDropdown, setShowSpecialistDropdown] = useState(false);
  const [showClinicDropdown, setShowClinicDropdown] = useState(false);

  // FIELD_OPTIONS removed - bulk import feature disabled

  const isSuperadmin = user?.role === 'superadmin';

  useEffect(() => {
    loadDoctors();
    if (isSuperadmin) {
      loadClinics();
      loadPopularDoctors();
    }
    // eslint-disable-next-line
  }, [onlineOnlyFilter, specialistFilter, selectedClinics]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.specialist-dropdown-container') && !e.target.closest('.clinic-dropdown-container')) {
        setShowSpecialistDropdown(false);
        setShowClinicDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDoctors = async () => {
    try {
      setLoading(true);
      let url = '/doctors?active_only=false';
      if (onlineOnlyFilter) url += '&available_online=true';
      if (specialistFilter && specialistFilter !== 'all') url += `&specialist=${encodeURIComponent(specialistFilter)}`;
      
      // Handle multi-clinic filter for superadmin
      if (isSuperadmin && selectedClinics.length > 0) {
        selectedClinics.forEach(clinicId => {
          url += `&clinic_filter=${clinicId}`;
        });
      }
      
      const data = await api.get(url);
      setDoctors(data);
    } catch (error) {
      console.error('Failed to load doctors:', error);
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const loadClinics = async () => {
    try {
      const data = await api.get('/clinics');
      setClinics(data);
    } catch (error) {
      console.error('Failed to load clinics:', error);
    }
  };

  const loadPopularDoctors = async () => {
    try {
      const data = await api.get(`/doctors/analytics/popular?limit=5${clinicFilter ? `&clinic_id=${clinicFilter}` : ''}`);
      setPopularDoctors(data);
    } catch (error) {
      console.error('Failed to load popular doctors:', error);
    }
  };

  const handleToggleActive = async (doctorId, isActive) => {
    try {
      if (isActive) {
        await api.delete(`/doctors/${doctorId}`);
        toast.success('Professional deactivated');
      } else {
        await api.post(`/doctors/${doctorId}/activate`);
        toast.success('Professional activated');
      }
      loadDoctors();
    } catch (error) {
      toast.error('Failed to update professional status');
    }
  };

  // handleFileUpload removed - bulk import feature disabled

  const filteredDoctors = doctors.filter(d =>
    d.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.email && d.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (d.specialist && d.specialist.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (d.nmc_id && d.nmc_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const renderConsultationTimings = (timings) => {
    if (!timings || Object.keys(timings).length === 0) return null;
    
    return (
      <div className="mt-2 p-2 bg-blue-50 rounded">
        <p className="font-semibold mb-1 flex items-center gap-1 text-xs">
          <Clock className="h-3 w-3" />
          Consultation Timings:
        </p>
        <div className="text-xs space-y-1">
          {Object.entries(timings).map(([day, sessions]) => (
            <div key={day}>
              <strong className="capitalize">{day}:</strong>
              {sessions.morning && <span> Morning: {sessions.morning}</span>}
              {sessions.day && <span> • Day: {sessions.day}</span>}
              {sessions.evening && <span> • Evening: {sessions.evening}</span>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#652D90" }}>
              Doctor Management
            </h1>
            <p className="text-gray-600 mt-1">{doctors.length} total professionals</p>
          </div>
          <div className="flex gap-2">
            {!isSuperadmin && (
              <>
                {/* Bulk Import Button REMOVED - feature disabled per user request */}
                <Button onClick={() => navigate('/doctors/add')} style={{ backgroundColor: "#652D90" }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Professional
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Popular Doctors Analytics (Superadmin Only) */}
        {isSuperadmin && popularDoctors.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <h2 className="text-lg font-semibold">Most Popular Doctors</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {popularDoctors.map((doc, idx) => (
                  <div key={doc.id} className="p-3 bg-gray-50 rounded border">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sm">{idx + 1}. {doc.full_name}</p>
                        {doc.specialist && (
                          <span className="text-xs px-2 py-0.5 rounded mt-1 inline-block" style={getSpecialistBadgeStyle(doc.specialist)}>
                            {doc.specialist}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-bold text-purple-600">{doc.popularity_score}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-2 grid grid-cols-3 gap-1">
                      <div>Patients: {doc.patients_count}</div>
                      <div>Appts: {doc.appointments_count}</div>
                      <div>F/ups: {doc.followups_count}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2 items-end flex-wrap">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, specialist, or NMC ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="w-48 relative specialist-dropdown-container">
            <Label className="text-xs mb-1">Specialist</Label>
            <div className="relative">
              <Input
                placeholder="Search specialist..."
                value={specialistSearch}
                onChange={(e) => {
                  setSpecialistSearch(e.target.value);
                  setShowSpecialistDropdown(true);
                }}
                onFocus={() => setShowSpecialistDropdown(true)}
                className="pr-8"
              />
              {specialistFilter && specialistFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => {
                    setSpecialistFilter('all');
                    setSpecialistSearch('');
                    setShowSpecialistDropdown(false);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            {showSpecialistDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                <div
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  data-testid="specialist-option-all"
                  onClick={() => {
                    setSpecialistFilter('all');
                    setSpecialistSearch('');
                    setShowSpecialistDropdown(false);
                  }}
                >
                  All Specialists
                </div>
                {MEDICAL_SPECIALISTS
                  .filter(spec => spec.toLowerCase().includes(specialistSearch.toLowerCase()))
                  .map(spec => (
                    <div
                      key={spec}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      data-testid={`specialist-option-${spec.replace(/\s+/g, '-')}`}
                      onClick={() => {
                        setSpecialistFilter(spec);
                        setSpecialistSearch(spec);
                        setShowSpecialistDropdown(false);
                      }}
                    >
                      {spec}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {isSuperadmin && (
            <div className="w-64 relative clinic-dropdown-container">
              <Label className="text-xs mb-1">Clinic Filter</Label>
              <div className="relative">
                <Input
                  placeholder="Search and select clinics..."
                  value={clinicSearch}
                  onChange={(e) => {
                    setClinicSearch(e.target.value);
                    setShowClinicDropdown(true);
                  }}
                  onFocus={() => setShowClinicDropdown(true)}
                  className="pr-8"
                />
                {selectedClinics.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedClinics([]);
                      setClinicSearch('');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear selection"
                  >
                    ✕
                  </button>
                )}
              </div>
              {selectedClinics.length > 0 && (
                <p className="text-xs text-gray-600 mt-1">
                  {selectedClinics.length} clinic{selectedClinics.length > 1 ? 's' : ''} selected
                </p>
              )}
              {showClinicDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer border-b">
                    <Checkbox
                      checked={selectedClinics.length === clinics.length && clinics.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedClinics(clinics.map(c => c.id));
                        } else {
                          setSelectedClinics([]);
                        }
                      }}
                    />
                    <span className="text-sm font-semibold">Select All Clinics</span>
                  </label>
                  {clinics
                    .filter(clinic => clinic.name.toLowerCase().includes(clinicSearch.toLowerCase()))
                    .map((clinic) => {
                      const duplicateCount = clinics.filter(c => c.name === clinic.name).length;
                      const displayName = duplicateCount > 1 
                        ? `${clinic.name} (${clinic.id.slice(-4)})`
                        : clinic.name;
                      const isSelected = selectedClinics.includes(clinic.id);
                      
                      return (
                        <label
                          key={clinic.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          data-testid={`clinic-option-${(clinic.name || '').replace(/\s+/g, '-')}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedClinics([...selectedClinics, clinic.id]);
                              } else {
                                setSelectedClinics(selectedClinics.filter(id => id !== clinic.id));
                              }
                            }}
                          />
                          <span className="text-sm">{displayName}</span>
                        </label>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={onlineOnlyFilter} onCheckedChange={setOnlineOnlyFilter} />
            <span className="text-sm">Available Online Only</span>
          </label>
        </div>

        {/* Doctor List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredDoctors.map((doctor) => (
              <Card key={doctor.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-lg">{doctor.full_name}</h3>
                          {doctor.is_verified && <CheckCircle className="h-4 w-4 text-green-500" title="Verified" />}
                          {doctor.professional_type === 'dietitian' && (
                            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">Dietitian</span>
                          )}
                          {!doctor.is_active && (
                            <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-600">Inactive</span>
                          )}
                          {doctor.specialist && (
                            <span className="text-xs px-2 py-1 rounded font-medium" style={getSpecialistBadgeStyle(doctor.specialist)}>
                              {doctor.specialist}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          {isSuperadmin && doctor.clinic_name && (
                            <p className="text-purple-600 font-semibold"><strong>Clinic:</strong> {doctor.clinic_name}</p>
                          )}
                          {doctor.nmc_id && (
                            <p><strong>{doctor.professional_type === 'dietitian' ? 'ID:' : 'NMC/Association ID:'}</strong> {doctor.nmc_id}</p>
                          )}
                          {doctor.department && <p><strong>Department:</strong> {doctor.department}</p>}
                          {doctor.email && <p><strong>Email:</strong> {doctor.email}</p>}
                          {doctor.mobile_number && <p><strong>Phone:</strong> {doctor.mobile_number}</p>}
                          {doctor.years_of_experience && <p><strong>Experience:</strong> {doctor.years_of_experience} years</p>}
                          <p><strong>Patients:</strong> {doctor.assigned_patients_count || 0}</p>
                          
                          {renderConsultationTimings(doctor.consultation_timings)}
                          
                          {(doctor.opd_charge_nepali || doctor.opd_charge_international || 
                            doctor.online_charge_nepali || doctor.online_charge_international) && (
                            <div className="mt-2 p-2 bg-gray-50 rounded">
                              <p className="font-semibold mb-1 text-xs">Consultation Charges (NPR):</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {doctor.opd_charge_nepali && <p><strong>OPD (Nepali):</strong> NPR {doctor.opd_charge_nepali}</p>}
                                {doctor.opd_charge_international && <p><strong>OPD (Intl):</strong> NPR {doctor.opd_charge_international}</p>}
                                {doctor.online_charge_nepali && <p><strong>Online (Nepali):</strong> NPR {doctor.online_charge_nepali}</p>}
                                {doctor.online_charge_international && <p><strong>Online (Intl):</strong> NPR {doctor.online_charge_international}</p>}
                              </div>
                            </div>
                          )}
                          
                          {doctor.available_online && <p className="text-green-600">🟢 Available for online consultation</p>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {!isSuperadmin && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/doctors/edit/${doctor.id}`)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(doctor.id, doctor.is_active)}
                            className={doctor.is_active ? "text-red-600" : "text-green-600"}
                          >
                            {doctor.is_active ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredDoctors.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center text-gray-500">
                  <UserCog className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No professionals found.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
